// src/components/Finance/BankAdjustModal.jsx
//
// Restricted adjustment feature (middleware/restrictedAdjustmentAccess.js on
// the server; usePermissions().canAdjustRestricted on this side): edits an
// existing Bank Issued transaction, or a Bank Receive that was classified as
// an Other Ledger, including entering a negative amount to correct a wrongly
// recorded one. Edit-only - creating a NEW Bank Received/Issued transaction
// stays on BankTransactionModal, open to every Finance writer as before.
import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLandmark, faMoneyBillTransfer, faSpinner, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import useDialog from '../common/ui/useDialog';
import './FinanceForm.css';
import './StaffLoans.css'; // shared modal look (sl-modal*)

const BankAdjustModal = ({ mode = 'issued', record, onClose, onSaved }) => {
  const isIssued = mode === 'issued';
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  useDialog(dialogRef, { open: true, onClose: submitting ? undefined : onClose });
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    date: record.date,
    amount: String(record.amount),
    reference_number: record.reference_number || '',
    remark: record.remark || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.date) return setError('Date is required');
    const amt = parseFloat(formData.amount);
    if (!amt) return setError('Amount must not be zero');

    setSubmitting(true);
    try {
      await api.put(`/finance/bank/${isIssued ? 'issued' : 'receive'}/${record.id}`, {
        date: formData.date,
        amount: amt,
        reference_number: formData.reference_number || null,
        remark: formData.remark || null
      });
      if (onSaved) onSaved(isIssued ? 'Bank issued updated successfully!' : 'Bank receipt updated successfully!', record.bank_id);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to update bank ${isIssued ? 'issued' : 'receipt'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sl-modal-overlay">
      <div ref={dialogRef} className="sl-modal" role="dialog" aria-modal="true" aria-labelledby="ba-title">
        <div className="sl-modal-header">
          <h3 id="ba-title">
            <FontAwesomeIcon
              icon={isIssued ? faMoneyBillTransfer : faLandmark}
              style={{ color: isIssued ? '#dc2626' : '#16a34a', marginRight: '8px' }}
            />
            Edit Bank {isIssued ? 'Issued' : 'Received'} ({record.other_ledger_name})
          </h3>
          <button type="button" className="sl-modal-close" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="sl-modal-body">
            <div className="sl-summary-card">
              <div className="sl-summary-row">
                <span className="sl-summary-lbl">
                  {record.bank_name}{record.account_number ? ` — ${record.account_number}` : ''}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>Date <span className="required">*</span></label>
              <input type="date" name="date" value={formData.date} onChange={handleChange} className="form-input" autoFocus />
            </div>
            <div className="form-group">
              <label>Amount (Rs.) <span className="required">*</span></label>
              <input type="number" name="amount" step="0.01" inputMode="decimal" value={formData.amount} onChange={handleChange} placeholder="0.00" className="form-input" />
              <span className="fl-field-hint">A negative amount corrects a wrongly recorded transaction.</span>
            </div>
            <div className="form-group">
              <label>Reference No (Optional)</label>
              <input type="text" name="reference_number" value={formData.reference_number} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>Remark</label>
              <textarea name="remark" rows={2} value={formData.remark} onChange={handleChange} placeholder="Add a note..." className="form-textarea" />
            </div>
            {error && <div className="sl-modal-error" role="alert">{error}</div>}
          </div>
          <div className="sl-modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                : <><FontAwesomeIcon icon={faCheck} /> Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAdjustModal;
